import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency, formatPercent } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Percent, UserCheck, Info, ExternalLink, FileText, BarChart3 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const MONTH_LABELS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};
const STATUS_LABELS = {
  pending: "À venir",
  in_progress: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
};

export const CommissionsTab = ({ employee }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("all"); // "all" or "YYYY"

  useEffect(() => {
    if (!employee?.id) return;
    setLoading(true);
    const params = year !== "all" ? { year: parseInt(year) } : undefined;
    api
      .getEmployeeCommissions(employee.id, params)
      .then((res) => setData(res.data))
      .catch(() => setData({ entries: [], orders_detail: [], seller_id: null }))
      .finally(() => setLoading(false));
  }, [employee?.id, year]);

  // Années disponibles : année courante + 4 ans en arrière
  const availableYears = useMemo(() => {
    const current = new Date().getFullYear();
    return [current, current - 1, current - 2, current - 3, current - 4];
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data || !data.seller_id) {
    return (
      <Card data-testid="commissions-unlinked">
        <CardContent className="py-10 text-center space-y-3">
          <div className="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center mx-auto">
            <Info className="h-6 w-6 text-indigo-500" />
          </div>
          <div className="font-medium">Aucun vendeur lié à ce salarié</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Pour afficher l'historique des commandes et des commissions de ce salarié, rattachez-le à un vendeur
            depuis la page <strong>Vendeurs</strong> (bouton « Modifier » sur la carte du vendeur,
            champ « Lié au salarié »).
          </p>
        </CardContent>
      </Card>
    );
  }

  const entries = data.entries || [];
  const ordersDetail = data.orders_detail || [];
  const totalPaid = data.total_paid_commission || 0;
  const totalOrders = data.total_orders || 0;

  return (
    <div className="space-y-4" data-testid="commissions-tab">
      {/* Header summary */}
      <Card className="border-indigo-200 bg-indigo-50/40">
        <CardContent className="py-4 flex flex-wrap items-center gap-6 justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <UserCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Vendeur rattaché</div>
              <div className="font-medium" data-testid="commissions-seller-name">
                {data.seller_name || "—"}
              </div>
            </div>
          </div>
          <div className="flex gap-6">
            <div>
              <div className="text-xs text-muted-foreground">Commissions cumulées</div>
              <div className="text-2xl font-bold text-indigo-700" data-testid="commissions-total-amount">
                {formatCurrency(totalPaid)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Commandes générées</div>
              <div className="text-2xl font-bold">{totalOrders}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Year filter + mini chart */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            Commissions par mois
            {year !== "all" && (
              <Badge variant="outline" className="ml-2 border-primary/40 text-primary">
                {year}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Année</span>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-32" data-testid="commissions-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {availableYears.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Aucune commission pour {year === "all" ? "ce vendeur" : `l'année ${year}`}.
            </div>
          ) : (
            <div className="h-48" data-testid="commissions-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...entries]
                    .reverse()
                    .map((e) => ({
                      label: `${MONTH_LABELS[e.month - 1].slice(0, 3)} ${String(e.year).slice(2)}`,
                      commission: e.commission,
                      orders_count: e.orders_count,
                    }))}
                  margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                  />
                  <Tooltip
                    formatter={(value, name) =>
                      name === "commission"
                        ? [formatCurrency(value), "Commission"]
                        : [value, "Commandes"]
                    }
                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  />
                  <Bar dataKey="commission" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly rollup */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Percent className="h-4 w-4 text-primary" />
            Cumul mensuel des commissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Aucune commission enregistrée pour ce vendeur.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Période</TableHead>
                    <TableHead className="text-right">Taux</TableHead>
                    <TableHead className="text-right">Commandes</TableHead>
                    <TableHead className="text-right">CA HT payé</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={`${e.year}-${e.month}`} data-testid={`commission-row-${e.year}-${e.month}`}>
                      <TableCell className="font-medium">
                        {MONTH_LABELS[e.month - 1]} {e.year}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="border-primary/40 text-primary">
                          {formatPercent(e.rate)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{e.orders_count}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(e.paid_ht)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-indigo-700">
                        {formatCurrency(e.commission)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-order detail */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Détail des commandes vendues ({ordersDetail.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ordersDetail.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Aucune commande vendue donnant droit à commission.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Référence</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Montant HT</TableHead>
                    <TableHead className="text-right">CA HT payé</TableHead>
                    <TableHead className="text-right">Taux</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ordersDetail.map((o) => (
                    <TableRow key={o.order_id} data-testid={`commission-order-${o.order_id}`}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {o.reference_date
                          ? new Date(o.reference_date).toLocaleDateString("fr-FR")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => navigate(`/commandes?focus=${o.order_id}`)}
                          className="inline-flex items-center gap-1 text-primary hover:underline underline-offset-2 font-medium"
                          data-testid={`commission-order-link-${o.order_id}`}
                          title="Voir la commande"
                        >
                          {o.reference || o.order_id.slice(0, 8)}
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{o.client_name || "—"}</div>
                        {o.city && (
                          <div className="text-[11px] text-muted-foreground">{o.city}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[o.status] || "bg-slate-100"}>
                          {STATUS_LABELS[o.status] || o.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(o.amount_ht)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(o.paid_ht)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {formatPercent(o.rate)}
                        {o.validation_ratio < 1 && (
                          <div className="text-[10px] text-amber-700">
                            × {Math.round(o.validation_ratio * 100)}%
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-indigo-700">
                        {formatCurrency(o.commission)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommissionsTab;
